var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import './polyfill';
import { ModalState, } from './useModal.interface';
var ESCAPE_KEY = 'Escape';
var DEFAULT_CONFIG = {
    animationDuration: 300,
    animated: false,
    persistent: false,
    open: false,
    onClose: function () { },
    value: undefined,
};
var useSSRLayoutEffect = typeof window === 'object' ? React.useLayoutEffect : React.useEffect;
var useMergedRef = function (ref) {
    var innerRef = React.useRef(null);
    return (ref ? ref : innerRef);
};
var useHasAlreadyBeenOpened = function (open) {
    var hasAlreadyBeenOpened = React.useRef(false);
    if (!hasAlreadyBeenOpened.current && open) {
        hasAlreadyBeenOpened.current = true;
    }
    return hasAlreadyBeenOpened.current;
};
var useDelayedOpen = function (open, animated) {
    var hasAlreadyRendered = React.useRef(false);
    var shouldDelayOpening = !hasAlreadyRendered.current && animated && open;
    var _a = React.useState(!shouldDelayOpening), canBeOpened = _a[0], setCanBeOpened = _a[1];
    React.useEffect(function () {
        hasAlreadyRendered.current = true;
        if (!canBeOpened) {
            setCanBeOpened(true);
        }
    }, [canBeOpened]);
    return canBeOpened && open;
};
var useValue = function (value, state) {
    var valueRef = React.useRef(value);
    if ([ModalState.opened, ModalState.opening].includes(state)) {
        valueRef.current = value;
    }
    return valueRef.current;
};
var useModal = function (baseConfig) {
    var config = __assign(__assign({}, DEFAULT_CONFIG), baseConfig);
    var open = useDelayedOpen(config.open, config.animated);
    var hasAlreadyBeenOpened = useHasAlreadyBeenOpened(open);
    var domRef = useMergedRef(config.ref);
    var configRef = React.useRef(config);
    var _a = React.useState(false), isLocalOpened = _a[0], setLocalOpened = _a[1];
    var state = React.useMemo(function () {
        if (!open && !isLocalOpened) {
            return ModalState.closed;
        }
        if (!open && isLocalOpened) {
            return ModalState.closing;
        }
        if (open && !isLocalOpened) {
            return ModalState.opening;
        }
        return ModalState.opened;
    }, [open, isLocalOpened]);
    var value = useValue(config.value, state);
    React.useEffect(function () {
        configRef.current = config;
    });
    useSSRLayoutEffect(function () {
        if (!configRef.current.animated && open !== isLocalOpened) {
            setLocalOpened(open);
        }
    }, [isLocalOpened, open]);
    var handleClose = React.useCallback(function () {
        configRef.current.onClose();
    }, []);
    React.useEffect(function () {
        if (configRef.current.animated) {
            var timeout_1 = setTimeout(function () { return setLocalOpened(open); }, configRef.current.animationDuration);
            return function () { return clearTimeout(timeout_1); };
        }
    }, [open]);
    React.useEffect(function () {
        var handleKeyDown = function (e) {
            if (!configRef.current.persistent &&
                configRef.current.open &&
                e.key === ESCAPE_KEY) {
                handleClose();
            }
        };
        var handleClick = function (e) {
            var target = e.target;
            if (!configRef.current.persistent &&
                state === ModalState.opened &&
                domRef.current &&
                target &&
                target.isConnected &&
                !domRef.current.contains(target)) {
                handleClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('click', handleClick);
        return function () {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('click', handleClick);
        };
    }, [domRef, handleClose, isLocalOpened, state]);
    return {
        state: state,
        close: handleClose,
        ref: domRef,
        hasAlreadyBeenOpened: hasAlreadyBeenOpened,
        value: value,
        animated: config.animated,
        animationDuration: config.animationDuration,
        persistent: config.persistent,
    };
};
export default useModal;
//# sourceMappingURL=useModal.js.map